
import {
  defineComponent, watch, computed, ref, onBeforeUnmount,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import AppItemWrapper from '@/components/AppItemWrapper.vue';
import {
  ICase, IData, IList, IResponse,
} from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '../store/driveStore';
import { useProfileStore } from '@/modules/profile/store/profileStore';
import AppSkeletonCase from '@/components/skeletons/AppSkeletonCase.vue';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import DriveService from '../services/DriveService';
import 'swiper/swiper-bundle.css';
import { NAVIGATION_PAGES } from '@/types/enums';

export default defineComponent({
  components: {
    ItemCase,
    AppItemWrapper,
    AppSkeletonCase,
    Swiper,
    SwiperSlide,
  },
  props: {
    title: { type: String, required: true },
    isUserPublicItems: { type: Boolean, default: false },
    skeleton: { type: Boolean, default: false },
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const profileStore = useProfileStore();

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage, onCacheScroll,
    } = UsePagination();

    const dataLoading = ref(false);
    const searchCases = ref<ICase[]>([]);

    const isMyProfile = computed(() => route.meta?.navigationPage === NAVIGATION_PAGES.PROFILE_MY);

    const currentUser = computed(() => mainStore.user.accountId);
    const profileId = computed(() => route.params.id as string);

    const casesLastPage = computed(() => (props.isUserPublicItems ? driveStore.userPublicCases.lastPage : driveStore.publicCases.lastPage));
    const casesCurrentPage = computed(() => (props.isUserPublicItems ? driveStore.userPublicCases.page : driveStore.publicCases.page));

    const cases = computed(() => {
      if (driveStore.search) {
        return searchCases.value.length > 50 ? searchCases.value.slice(0, 50) : searchCases.value;
      }
      const data = props.isUserPublicItems ? driveStore.userPublicCases.data : driveStore.publicCases.data;
      return data.length > 50 ? data.slice(0, 50) : data;
    });

    const publicCasesAmount = computed(() => {
      if (route.meta?.navigationPage === NAVIGATION_PAGES.HOME) {
        return 4;
      } if (isMyProfile.value) {
        return mainStore.user.publicCasesAmount;
      }
      return route.query.publicCasesAmount;
    });

    function fetchCacheData() {
      if (props.isUserPublicItems && isMyProfile.value) {
        return driveStore.fetchUserPublicCases(mainStore.user.accountId);
      }
      if (props.isUserPublicItems) {
        return driveStore.fetchUserPublicCases(route.params.id);
      }
      return driveStore.fetchPublicCases();
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      if (props.isUserPublicItems && isMyProfile.value) {
        return DriveService.getUserPublicCases(pageIdx.value, driveStore.search, mainStore.user.accountId as string)
          .then((res) => {
            let casesResponse: any = { ...res };
            casesResponse.results.data.items = casesResponse.results.data.items.map((item: any) => ({ ...item.case as ICase, publicAccessed: item.publicAccessed }));

            casesResponse = casesResponse as IResponse<IData<IList<ICase>>>;

            if (pageIdx.value === 0) {
              searchCases.value = casesResponse.results.data.items;
            } else {
              searchCases.value.push(...casesResponse.results.data.items);
            }

            return casesResponse;
          });
      } if (props.isUserPublicItems) {
        return DriveService.getUserPublicCases(pageIdx.value, driveStore.search, route.params.id as string)
          .then((res) => {
            let casesResponse: any = { ...res };
            casesResponse.results.data.items = casesResponse.results.data.items.map((item: any) => ({ ...item.case as ICase, publicAccessed: item.publicAccessed }));

            casesResponse = casesResponse as IResponse<IData<IList<ICase>>>;

            if (pageIdx.value === 0) {
              searchCases.value = casesResponse.results.data.items;
            } else {
              searchCases.value.push(...casesResponse.results.data.items);
            }

            return casesResponse;
          });
      }
      return DriveService.getPublicCases(pageIdx.value, driveStore.search)
        .then((response) => {
          if (currentSearch !== driveStore.search) {
            return Promise.reject(new Error('response is out of date'));
          }
          if (pageIdx.value === 0) {
            searchCases.value = response.results.data.items;
          } else {
            searchCases.value.push(...response.results.data.items);
          }
          return response;
        });
    }

    function handleScroll(lastSlide?: boolean) {
      if (driveStore.search) {
        if (pageIdx.value >= 2) {
          return;
        }
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        if (casesCurrentPage.value >= 2 || casesCurrentPage.value <= 1) {
          return;
        }
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, casesLastPage.value, casesCurrentPage.value, false, lastSlide);
      }
    }

    function onCaseClick(iCase: ICase) {
      driveStore.resetCurrentCase();
      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = iCase.photosAmount;
      driveStore.currentCase.filesAmount = iCase.filesAmount;

      if (iCase.owner.accountId !== currentUser.value) {
        router.push(`${iCase.externalUrl}`);
      } else {
        router.push(`/drive/case/my/${iCase.id}/view/`);
      }
    }

    function viewAllCases() {
      if (props.isUserPublicItems && isMyProfile.value) {
        router.push('/drive/my/public/cases');
      } else if (props.isUserPublicItems) {
        router.push(`/drive/${route.params.id}/public/cases`);
      } else {
        router.push('/drive/public/cases');
      }
    }

    onBeforeUnmount(() => {
      driveStore.userPublicCases.page = 0;
    });

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      driveStore.dataLoading.cases = true;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
            driveStore.dataLoading.cases = false;
          });
      }, 1000);
    });

    return {
      mainStore,
      driveStore,
      dataLoading,
      cases,
      scroller,
      isPageLoading,
      handleScroll,
      onCaseClick,
      viewAllCases,
      profileStore,
      publicCasesAmount,
    };
  },
});
